import {
  errorResult,
  normalizePhysicalExamEntry,
  PhysicalExamEntry,
  PhysicalExamEntryData,
  PHYSICAL_EXAM_ENTRY_VERSION,
  Result,
  Source,
  successResult,
  tu,
} from "beitary-shared";
import {
  collection,
  deleteDoc,
  doc,
  Firestore,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  Unsubscribe,
  updateDoc,
} from "firebase/firestore";

// add organization consultation

interface AddPhysicalExamEntry {
  ({
    db,
    organizationId,
    consultationId,
    authorId,
    authorName,
    source,
    data,
  }: {
    db: Firestore;
    organizationId: string;
    consultationId: string;
    authorId: string;
    authorName: string;
    source: Source;
    data: PhysicalExamEntryData;
  }): Promise<Result<PhysicalExamEntry | null>>;
}

const addPhysicalExamEntry: AddPhysicalExamEntry = async ({
  db,
  organizationId,
  consultationId,
  authorId,
  authorName,
  source,
  data,
}) => {
  try {
    // create a new ref to get a new consultation id
    const newRef = doc(
      collection(
        db,
        "organizations",
        organizationId,
        "consultations",
        consultationId,
        "physical-exams"
      )
    );

    const newObj: PhysicalExamEntry = normalizePhysicalExamEntry({
      ...data,
      id: newRef.id,
      authorId,
      authorName,
      version: PHYSICAL_EXAM_ENTRY_VERSION,
      source,
      createdAt: tu.getCurrentDateTime(),
      lastUpdatedAt: tu.getCurrentDateTime(),
    });

    await setDoc(newRef, newObj);

    // t("PHYSICAL_EXAM_ENTRY_CREATED")
    const successMessage = "PHYSICAL_EXAM_ENTRY_CREATED";
    return successResult({
      message: successMessage,
      payload: newObj,
    });
  } catch (err: any) {
    return errorResult({ message: err.message });
  }
};

interface UpdatePhysicalExamEntry {
  ({
    db,
    organizationId,
    consultationId,
    authorId,
    authorName,
    id,
    source,
    data,
  }: {
    db: Firestore;
    organizationId: string;
    consultationId: string;
    authorId: string;
    authorName: string;
    id: string;
    source: Source;
    data: Partial<PhysicalExamEntryData>;
  }): Promise<Result<Partial<PhysicalExamEntryData> | null>>;
}

const updatePhysicalExamEntry: UpdatePhysicalExamEntry = async ({
  db,
  organizationId,
  consultationId,
  authorId,
  authorName,
  id,
  source,
  data,
}) => {
  try {
    const docRef = doc(
      db,
      "organizations",
      organizationId,
      "consultations",
      consultationId,
      "physical-exams",
      id
    );

    const updates: Partial<PhysicalExamEntry> = {
      ...data,
      authorId,
      authorName,
      version: PHYSICAL_EXAM_ENTRY_VERSION,
      source,
      createdAt: tu.getCurrentDateTime(),
      lastUpdatedAt: tu.getCurrentDateTime(),
    };

    await updateDoc(docRef, updates);

    // t("PHYSICAL_EXAM_ENTRY_UPDATED")
    const successMessage = "PHYSICAL_EXAM_ENTRY_UPDATED";

    return successResult({
      message: successMessage,
      payload: updates,
    });
  } catch (err: any) {
    console.log(err.message);
    return errorResult({ message: err.message });
  }
};

interface DeletePhysicalExamEntry {
  ({
    db,
    organizationId,
    consultationId,
    id,
  }: {
    db: Firestore;
    organizationId: string;
    consultationId: string;
    id: string;
  }): Promise<Result<boolean | null>>;
}

const deletePhysicalExamEntry: DeletePhysicalExamEntry = async ({
  db,
  organizationId,
  consultationId,
  id,
}) => {
  try {
    const docRef = doc(
      db,
      "organizations",
      organizationId,
      "consultations",
      consultationId,
      "physical-exams",
      id
    );

    try {
      await deleteDoc(docRef);
      // t("PHYSICAL_EXAM_ENTRY_DELETED")
      const successMessage = "PHYSICAL_EXAM_ENTRY_DELETED";
      return successResult({
        message: successMessage,
        payload: true,
      });
    } catch (error: any) {
      console.log(error);
      return errorResult({ message: error.message });
    }
  } catch (err: any) {
    console.log(err);
    return errorResult({ message: err.message });
  }
};

// get organization physicalExam listener
interface GetPhysicalExamEntrysListenerCallback {
  (physicalExam: PhysicalExamEntry[]): void;
}
interface GetPhysicalExamEntrysListener {
  db: Firestore;
  organizationId: string;
  consultationId: string;
  callback: GetPhysicalExamEntrysListenerCallback;
}

const getPhysicalExamEntrysListener = ({
  db,
  organizationId,
  consultationId,
  callback,
}: GetPhysicalExamEntrysListener): Unsubscribe => {
  try {
    // console.log("getPhysicalExamEntrysListener: new listener");
    const newQuery = query(
      collection(
        db,
        "organizations",
        organizationId,
        "consultations",
        consultationId,
        "physical-exams"
      ),
      orderBy("lastUpdatedAt", "desc")
    );
    return onSnapshot(newQuery, (querySnapshot) => {
      const physicalExam: PhysicalExamEntry[] = [];
      querySnapshot.forEach((doc) => {
        try {
          physicalExam.push(normalizePhysicalExamEntry(doc.data()));
        } catch (err) {
          console.log(err);
        }
      });
      callback(physicalExam);
    });
  } catch (err: any) {
    console.log(err);
    return () => {};
  }
};

// we inject dependencies to improve testability
export const physicalExams = ({
  authorId,
  authorName,
  consultationId,
  db,
  organizationId,
  source,
}: {
  db: Firestore;
  organizationId: string;
  consultationId: string;
  authorId: string;
  authorName: string;
  source: Source;
}) => {
  return {
    getPhysicalExamEntrysListener: (
      callback: GetPhysicalExamEntrysListenerCallback
    ) =>
      getPhysicalExamEntrysListener({
        db,
        organizationId,
        consultationId,
        callback,
      }),
    addPhysicalExamEntry: (data: PhysicalExamEntryData) =>
      addPhysicalExamEntry({
        db,
        organizationId,
        consultationId,
        authorId,
        authorName,
        source,
        data,
      }),
    updatePhysicalExamEntry: (
      id: string,
      data: Partial<PhysicalExamEntryData>
    ) =>
      updatePhysicalExamEntry({
        db,
        organizationId,
        consultationId,
        authorId,
        authorName,
        id,
        source,
        data,
      }),
    deletePhysicalExamEntry: (id: string) =>
      deletePhysicalExamEntry({
        db,
        organizationId,
        consultationId,
        id,
      }),
  };
};
